import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Link,
  CircularProgress,
} from '@material-ui/core';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import { makeStyles } from '@material-ui/core/styles';
import StatusPill from './StatusPill';
import RowActionButton from './RowActionButton';
import { VIEWS } from '../helpers/statusConfig';

const useStyles = makeStyles(() => ({
  wrap: {
    marginTop: 12,
    border: '1px solid #E0E0E0',
    borderRadius: 4,
    overflowX: 'auto',
    boxShadow: 'none',
  },
  headCell: {
    backgroundColor: '#eee',
    fontWeight: 'bold',
    // whiteSpace: 'nowrap',
    height: '28px',
    padding: '10px',
    zIndex: 3,
  },
  cell: {
    padding: '10px',
    borderBottom: '1px solid #F0F0F0',
  },
  empty: {
    padding: 48,
    textAlign: 'center',
    color: '#9E9E9E',
  },
  messagesLink: {
    color: '#173072',
    cursor: 'pointer',
    textDecoration: 'underline',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  twoDates: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.2,
  },
  dateLabel: {
    fontSize: '0.7rem',
    color: '#9E9E9E',
  },
}));

// align: left = alphanumeric, right = numeric, center = status / action / date
const PreScheduleColumns = [
  { key: 'scheduleAgreementNumber', label: 'PO Number', align: 'left' },
  { key: 'deliveryAddresseeCountry', label: 'Country', align: 'left' },
  { key: 'productCodeDesc', label: 'Product Code / Description', align: 'left' },
  { key: 'brand', label: 'Brand', align: 'left' },
  { key: 'vendorBatchNumber', label: 'Batch Number', align: 'left' },
  { key: 'producedQuantity', label: 'Qty.', align: 'right' },
  { key: 'batchUOM', label: 'UOM', align: 'left' },
  { key: 'plNumber', label: 'PL Number', align: 'left' },
  { key: 'shipmentStatus', label: 'Shipment Status', align: 'center' },
  // { key: 'sponsorShipmentID', label: 'Sponsor Shipment ID', align: 'left' },
  { key: 'action', label: 'Actions', align: 'center' },
];

const PostShipmentColumns = [
  // Post-Schedule view: both IDs are now labelled "Shipment ID" (was "… Schedule ID").
  { key: 'sponsorShipmentID', label: 'Sponsor Shipment ID', align: 'left' },
  // { key: 'freightForwarderScheduleID', label: 'Freight Forwarder Schedule ID', align: 'left' },
  { key: 'freightForwarderScheduleID', label: 'Freight Forwarder Shipment ID', align: 'left' },
  //{ key: 'actualDispatchDate', label: 'Dispatch Date', align: 'center' },
  { key: 'plannedDispatchDate', label: 'Expected Dispatch Date', align: 'center' },
  { key: 'viewMessages', label: 'View Shipment Messages', align: 'center' },
  { key: 'shipmentStatus', label: 'Shipment Status', align: 'center' },
  { key: 'action', label: 'Actions', align: 'center' },
];

const ThreePLColumns = [
  { key: 'sponsorShipmentID', label: 'Sponsor Shipment ID', align: 'left' },
  { key: 'freightForwarderScheduleID', label: 'Freight Forwarder Shipment ID', align: 'left' },
  { key: 'netWeight', label: 'Net Weight', align: 'right' },
  { key: 'tempRangeAndSetPoint', label: 'Temperature Requirement', align: 'left' },
  { key: 'dispatchDates', label: 'Planned / Actual Dispatch Date', align: 'center' },
  { key: 'arrivalDates', label: 'Estimated / Actual Arrival Date', align: 'center' },
  { key: 'shipmentStatus', label: 'Shipment Status', align: 'center' },
  { key: 'action', label: 'Actions', align: 'center' },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const columnsForView = (view) => {
  if (view === VIEWS.POST_SCHEDULE) return PostShipmentColumns;
  if (view === VIEWS.POST_DELIVERY) return ThreePLColumns;
  return PreScheduleColumns;
};

const ShipmentsTable = ({
  view,
  rows,
  loading,
  error,
  role,
  onAction,
  onComment,
  onOpenMessages,
  onRowClick,
}) => {
  const classes = useStyles();
  const columns = columnsForView(view);

  const renderCell = (col, row) => {
    switch (col.key) {
      case 'productCodeDesc':
        return (
          <>
            {row.fgMaterialCode && row.fgMaterialName ?
              <span className={classes.productDesc}>{row.fgMaterialCode} / {row.fgMaterialName}</span>
              : ""}
          </>
        );
      case 'shipmentStatus':
        return <StatusPill statusId={row.statusID} statusDescription={row.statusDescription} overlays={row.overlays} />;
      case 'action':
        return (
          <RowActionButton
            row={row}
            role={role}
            view={view}
            onAction={onAction}
            onComment={onComment}
          />
        );
      case 'viewMessages':
        return (
          <Link
            component="button"
            className={classes.messagesLink}
            onClick={() => onOpenMessages(row)}
          >
            Click to View
          </Link>
        );
      case 'tempRangeAndSetPoint': {
        // Post-Shipment view: temperature lives on each batch, not the row
        const temps = Array.isArray(row.batchesList)
          ? [...new Set(row.batchesList.map((b) => b.tempRangeAndSetPoint).filter(Boolean))]
          : [];
        return temps.length ? temps.join(', ') : (row.tempRangeAndSetPoint || ' ');
      }
      case 'actualDispatchDate':
        return formatDate(row.actualDispatchDate);
      case 'plannedDispatchDate':
        return formatDate(row.plannedDispatchDate);
      case 'estimatedArrivalDate':
        return formatDate(row.estimatedArrivalDate);
      case 'dispatchDates':
        return (
          <div className={classes.twoDates}>
            {!row.plannedDispatchDate && !row.actualDispatchDate ? '' :
              <>
                <span>{formatDate(row.plannedDispatchDate)} {row.actualDispatchDate && " / "}</span>
                {row.actualDispatchDate && <span>{formatDate(row.actualDispatchDate)}</span>}
              </>}
          </div>
        );
      case 'arrivalDates':
        return (
          <div className={classes.twoDates}>
            {!row.plannedDispatchDate && !row.actualDispatchDate ? '' :
              <>
                <span>{formatDate(row.estimatedArrivalDate)} {row.statusID === 64 && " / "}</span>
                {row.statusID === 64 && <span>{formatDate(row.estimatedArrivalDate)}</span>}
              </>}
          </div>
        );
      default:
        return (row[col.key] === '' || row[col.key] == null) ? ' ' : row[col.key];
    }
  };

  return (
    <TableContainer component={Paper} elevation={0} className={classes.wrap}>
      <Table size="small">
        <TableHead className="default-table-styles">
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.key} className={classes.headCell} align={c.align}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" style={{ padding: 36 }}>
                <CircularProgress size={28} />
              </TableCell>
            </TableRow>
          )}
          {!loading && error && (
            <TableRow>
              <TableCell colSpan={columns.length} className={classes.empty}>
                <Typography variant="body2">Something went wrong.</Typography>
              </TableCell>
            </TableRow>
          )}
          {!loading && !error && (!rows || rows.length === 0) && (
            <TableRow>
              <TableCell colSpan={columns.length} className={classes.empty}>
                <Typography variant="body2">No records found.</Typography>
              </TableCell>
            </TableRow>
          )}
          {!loading && !error && rows && rows.map((row) => {
            return (
              <TableRow
                key={row.sponsorShipmentID || row.schrockenDeliveryScheduleBatchID}
              >
                {columns.map((c) => (
                  <TableCell key={c.key} className={classes.cell} align={c.align}>
                    {renderCell(c, row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}

        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ShipmentsTable;
